/**
 * Strategy template commands — clone, build calls, propose, rebalance, status.
 *
 * Replaces the old strategy registry commands with template-based workflow:
 *   sherwood strategy list      — show available templates
 *   sherwood strategy clone     — clone + initialize a template
 *   sherwood strategy propose   — clone + init + build calls + submit proposal (all-in-one)
 *   sherwood strategy status    — read portfolio allocations + state
 *   sherwood strategy rebalance — sell-all/re-buy at current target weights (Portfolio only)
 */

import type { Command } from "commander";
import type { Address, Hex } from "viem";
import { parseUnits, formatUnits, isAddress, erc20Abi, encodeAbiParameters, decodeAbiParameters } from "viem";
import chalk from "chalk";
import ora from "ora";
import { writeFileSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";

import { getPublicClient, getAccount, writeContractWithRetry, waitForReceipt, formatContractError } from "../lib/client.js";
import { getChain, getExplorerUrl, getNetwork, CHAIN_REGISTRY } from "../lib/network.js";
import { getCachedSwapRoute, cacheSwapRoute, type SwapRoute } from "../lib/config.js";
import { TOKENS, MOONWELL, VENICE, AERODROME, UNISWAP, STRATEGY_TEMPLATES, SYNTHRA, CHAINLINK, ROBINHOOD_STOCK_ROUTES, robinhoodEquityMaxAge } from "../lib/addresses.js";
import { BASE_STRATEGY_ABI, PORTFOLIO_STRATEGY_ABI, SYNDICATE_VAULT_ABI, HYPERLIQUID_PERP_STRATEGY_ABI } from "../lib/abis.js";
import { cloneTemplate } from "../lib/clone.js";
import { HYPERLIQUID_GRID_STRATEGY_ABI } from "../grid/strategy-abi.js";
import type { BatchCall } from "../lib/batch.js";
import { formatBatch } from "../lib/batch.js";
import { isCalldataOnly, emitCalldata } from "../lib/calldata.js";
import { encodeStrategyDeploy, encodePropose, type ChainId } from "@sherwoodagent/sdk";

import * as moonwellBuilder from "../strategies/moonwell-supply-template.js";
import * as veniceBuilder from "../strategies/venice-inference-template.js";
import * as aerodromeBuilder from "../strategies/aerodrome-lp-template.js";
import * as wstethBuilder from "../strategies/wsteth-moonwell-template.js";
import * as mamoBuilder from "../strategies/mamo-yield-template.js";
import * as portfolioBuilder from "../strategies/portfolio-template.js";
import * as hyperliquidPerpBuilder from "../strategies/hyperliquid-perp-template.js";
import * as hyperliquidGridBuilder from "../strategies/hyperliquid-grid-template.js";

import { concat, numberToHex, size } from "viem";

const ZERO: Address = "0x0000000000000000000000000000000000000000";
const ZERO_BYTES32 =
  "0x0000000000000000000000000000000000000000000000000000000000000000" as Hex;

// ── Uniswap V3 swap routing helpers ──

/**
 * Encode Uniswap V3 packed path: token (20 bytes) + fee (3 bytes) + token (20 bytes) [+ fee + token ...]
 */
function encodeV3Path(tokens: Address[], fees: number[]): Hex {
  if (tokens.length !== fees.length + 1) throw new Error("tokens.length must be fees.length + 1");
  const parts: Hex[] = [];
  for (let i = 0; i < tokens.length; i++) {
    parts.push(tokens[i].toLowerCase() as Hex);
    if (i < fees.length) {
      // fee as 3 bytes (uint24)
      parts.push(numberToHex(fees[i], { size: 3 }));
    }
  }
  return concat(parts);
}

/**
 * Build swapExtraData for the UniswapSwapAdapter.
 *
 * Mode 0 — single-hop: 0x00 + abi.encode(uint24 fee)
 * Mode 1 — multi-hop:  0x01 + abi.encode(bytes path, uint16 perHopSlippageBps)
 *
 * Sherlock run #2 #11: mode-1 extraData now carries a per-hop slippage
 * budget so the adapter's `_chainedSingleHops` can enforce a per-hop
 * `amountOutMinimum` derived from a quoter pre-call at swap time. This
 * closes the cascading-sandwich attack on intermediate hops. Default
 * to 100 bps (1%) per hop unless the caller overrides — conservative
 * for typical V3 routes; mainnet templates with deeper pools can
 * tighten this. The adapter still enforces the top-level `amountOutMin`
 * on the final hop, so cumulative slippage is doubly capped.
 *
 * robinhood-testnet uses the registry UniswapSwapAdapter (Synthra-router-backed,
 * QuoterV2-shim), which consumes the SAME mode-prefixed encoding as the other
 * chains — mode 0 single-hop / mode 1 multi-hop — so no special-case here.
 */
function buildSwapExtraData(
  network: string,
  tokenIn: Address,
  tokenOut: Address,
  feeTier: number,
  hop?: { via: Address; feeIn: number; feeOut: number },
  perHopSlippageBps: number = 100,
): Hex {
  if (hop) {
    // Multi-hop: tokenIn → hop.via → tokenOut
    const path = encodeV3Path([tokenIn, hop.via, tokenOut], [hop.feeIn, hop.feeOut]);
    const encoded = encodeAbiParameters(
      [{ type: "bytes" }, { type: "uint16" }],
      [path, perHopSlippageBps],
    );
    return `0x01${encoded.slice(2)}` as Hex;
  }

  // Single-hop: mode 0
  const encoded = encodeAbiParameters([{ type: "uint24" }], [feeTier]);
  return `0x00${encoded.slice(2)}` as Hex;
}

// ── Explicit per-token swap-route spec parsing (--swap-routes) ──
//
// One entry per allocation, comma-separated at the flag level. Each entry:
//   'v3:<fee>'                          → mode 0 (v3 single-hop)
//   'v4:<fee>:<tickSpacing>'            → mode 2 (v4 single-hop hookless)
//   'v4path:<hop>+<hop>+...'            → mode 3 (v4 multi-hop hookless)
//       where hop = '<currency>@<fee>:<tickSpacing>' and
//       currency ∈ { 'eth'|'native' → address(0), 'self' → this allocation's
//       token, or a literal 0x-address }. The last hop's currency MUST resolve
//       to the allocation token (contract-enforced); 'self' is the ergonomic form.

function parseSwapRouteSpec(spec: string, token: Address): Hex {
  const s = spec.trim();
  const [kind, ...restParts] = s.split(":");
  const rest = restParts.join(":");
  switch (kind) {
    case "v3": {
      const fee = Number(rest);
      if (!Number.isInteger(fee) || fee <= 0) throw new Error(`bad v3 fee in '${spec}'`);
      return portfolioBuilder.v3SingleHopExtraData(fee);
    }
    case "v4": {
      const [feeStr, tsStr] = rest.split(":");
      const fee = Number(feeStr);
      const tickSpacing = Number(tsStr);
      if (!Number.isInteger(fee) || fee <= 0) throw new Error(`bad v4 fee in '${spec}'`);
      if (!Number.isInteger(tickSpacing) || tickSpacing <= 0) throw new Error(`bad v4 tickSpacing in '${spec}'`);
      return portfolioBuilder.v4SingleHopExtraData(fee, tickSpacing);
    }
    case "v4path": {
      const hops = rest.split("+").map((hopStr) => {
        const [currencyRaw, feeTs] = hopStr.split("@");
        if (!feeTs) throw new Error(`bad v4path hop '${hopStr}' in '${spec}' (expected currency@fee:tickSpacing)`);
        const [feeStr, tsStr] = feeTs.split(":");
        const fee = Number(feeStr);
        const tickSpacing = Number(tsStr);
        if (!Number.isInteger(fee) || fee <= 0) throw new Error(`bad v4path fee in '${spec}'`);
        if (!Number.isInteger(tickSpacing) || tickSpacing <= 0) throw new Error(`bad v4path tickSpacing in '${spec}'`);
        const c = currencyRaw.trim().toLowerCase();
        let currency: Address;
        if (c === "eth" || c === "native") currency = ZERO;
        else if (c === "self") currency = token;
        else if (isAddress(currencyRaw.trim())) currency = currencyRaw.trim() as Address;
        else throw new Error(`bad v4path currency '${currencyRaw}' in '${spec}' (use eth/native/self/0x-address)`);
        return { currency, fee, tickSpacing };
      });
      return portfolioBuilder.v4PathExtraData(hops);
    }
    default:
      throw new Error(`unknown swap-route kind '${kind}' in '${spec}' (use v3/v4/v4path)`);
  }
}

/**
 * Auto-detect swap route for a token pair on Uniswap V3.
 * Returns { direct: true, feeTier } or { direct: false, hop } or null if no route found.
 */
async function detectSwapRoute(
  asset: Address,
  token: Address,
  preferredFeeTier: number,
): Promise<{ extraData: Hex; routeDesc: string } | null> {
  const network = getNetwork();
  // Current deployment target (robinhood-testnet) uses a direct v3-style route
  // via the Synthra-backed adapter. Multi-chain v3 auto-detect (Base/HyperEVM)
  // was removed with those chains; re-add per-chain detection here when a chain
  // with distinct routing comes back.
  return {
    extraData: buildSwapExtraData(network, asset, token, preferredFeeTier),
    routeDesc: `direct (fee ${preferredFeeTier})`,
  };
}

// ── Template definitions ──

interface TemplateDef {
  name: string;
  key: string;
  description: string;
  addressKey: keyof ReturnType<typeof STRATEGY_TEMPLATES>;
}

const TEMPLATES: TemplateDef[] = [
  {
    name: "Moonwell Supply",
    key: "moonwell-supply",
    description: "Supply tokens to Moonwell lending market, earn yield",
    addressKey: "MOONWELL_SUPPLY",
  },
  {
    name: "Aerodrome LP",
    key: "aerodrome-lp",
    description: "Provide liquidity on Aerodrome DEX + optional gauge staking",
    addressKey: "AERODROME_LP",
  },
  {
    name: "Venice Inference",
    key: "venice-inference",
    description: "Stake VVV for sVVV — Venice private AI inference",
    addressKey: "VENICE_INFERENCE",
  },
  {
    name: "wstETH Moonwell Yield",
    key: "wsteth-moonwell",
    description: "WETH → wstETH → Moonwell — stack Lido + lending yield",
    addressKey: "WSTETH_MOONWELL",
  },
  {
    name: "Mamo Yield",
    key: "mamo-yield",
    description: "Deposit into Mamo for optimized yield across Moonwell + Morpho vaults",
    addressKey: "MAMO_YIELD",
  },
  {
    name: "Portfolio",
    key: "portfolio",
    description: "Weighted portfolio of tokens (stock tokens, crypto) with rebalancing",
    addressKey: "PORTFOLIO",
  },
  {
    name: "Hyperliquid Perp",
    key: "hyperliquid-perp",
    description: "Leveraged perp trading on Hyperliquid via HyperEVM precompiles",
    addressKey: "HYPERLIQUID_PERP",
  },
  {
    name: "Hyperliquid Grid",
    key: "hyperliquid-grid",
    description: "ATR-based grid trading on Hyperliquid via HyperEVM precompiles",
    addressKey: "HYPERLIQUID_GRID",
  },
];

function resolveTemplate(key: string): { def: TemplateDef; address: Address } {
  const def = TEMPLATES.find((t) => t.key === key);
  if (!def) {
    console.error(chalk.red(`Unknown template: ${key}`));
    console.error(chalk.dim(`Available: ${TEMPLATES.map((t) => t.key).join(", ")}`));
    process.exit(1);
  }
  const address = STRATEGY_TEMPLATES()[def.addressKey];
  if (address === ZERO) {
    console.error(chalk.red(`Template "${def.name}" not deployed on this network.`));
    process.exit(1);
  }
  return { def, address };
}

// ── Helpers for building init data per template ──

/**
 * Pure (no-RPC) derivation of the assets a propose call will pull from the
 * vault. Mirrors the asset/amount slice of `buildInitDataForTemplate` so the
 * propose preflight can balance-check the vault BEFORE clone + init burns
 * any gas. Returns one entry per token the vault has to fund — single
 * entry for most templates, two for aerodrome-lp (tokenA + tokenB).
 *
 * Templates whose amount is `0n` (hyperliquid-perp / -grid in dynamic-all
 * mode, wsteth-moonwell without --amount) intentionally return entries
 * with `amount === 0n`; the caller short-circuits on those.
 *
 * Keep in sync with `buildInitDataForTemplate` per-branch logic. Diverging
 * here only weakens the preflight guard — the real values still come from
 * `buildInitDataForTemplate`, so we'd just miss a warning, never produce
 * an inconsistent on-chain call.
 */
function computeProposalAssetChecks(
  templateKey: string,
  opts: Record<string, string | boolean | undefined>,
): { asset: Address; amount: bigint; label: string }[] {
  if (templateKey === "moonwell-supply" || templateKey === "mamo-yield") {
    if (!opts.amount) return [];
    const token = (opts.token as string) || "USDC";
    const asset = resolveToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    return [{ asset, amount: parseUnits(opts.amount as string, decimals), label: token.toUpperCase() }];
  }

  if (templateKey === "venice-inference") {
    if (!opts.amount) return [];
    const assetSymbol = (opts.asset as string) || "USDC";
    const asset = resolveToken(assetSymbol);
    const decimals = assetSymbol.toUpperCase() === "USDC" ? 6 : 18;
    return [{ asset, amount: parseUnits(opts.amount as string, decimals), label: assetSymbol.toUpperCase() }];
  }

  if (templateKey === "aerodrome-lp") {
    // Aerodrome needs both legs of the pair funded. We can't compute the
    // per-leg parseUnits without a decimals() RPC, so we fall back to a
    // raw-units balance check (vault must hold at least `amountA` raw of
    // tokenA AND `amountB` raw of tokenB). Worst case: a stricter check
    // than needed, never permissive.
    if (!opts.tokenA || !opts.tokenB || !opts.amountA || !opts.amountB) return [];
    // amountA/amountB are human strings — without decimals we can't normalize.
    // Skip the precise check for aerodrome and let buildInitDataForTemplate
    // handle errors. (Other templates cover the common --amount footgun.)
    return [];
  }

  if (templateKey === "wsteth-moonwell") {
    if (!opts.amount) return []; // dynamic-all mode
    return [{
      asset: TOKENS().WETH,
      amount: parseUnits(opts.amount as string, 18),
      label: "WETH",
    }];
  }

  if (templateKey === "portfolio") {
    if (!opts.amount) return [];
    // Keep this in lock-step with buildInitDataForTemplate's portfolio branch:
    // default asset is USDG on robinhood mainnet (6 dec, no USDC there), WETH on
    // robinhood-testnet, USDC elsewhere; decimals resolved from token metadata.
    const assetSymbol = (opts.asset as string) || defaultPortfolioAsset();
    const asset = resolveToken(assetSymbol);
    const decimals = assetDecimalsForSymbol(assetSymbol);
    return [{ asset, amount: parseUnits(opts.amount as string, decimals), label: assetSymbol.toUpperCase() }];
  }

  if (templateKey === "hyperliquid-perp" || templateKey === "hyperliquid-grid") {
    if (!opts.amount) return []; // dynamic-all mode — skip check
    const token = (opts.token as string) || "USDC";
    const asset = resolveToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    return [{ asset, amount: parseUnits(opts.amount as string, decimals), label: token.toUpperCase() }];
  }

  return [];
}

/**
 * Parse a comma-separated per-allocation override flag (--feed-ids,
 * --price-decimals, --swap-routes, --max-price-ages) into a slot-aligned array.
 *
 * Empty entries are PRESERVED (trimmed to ""), NOT filtered — so partial
 * overrides work: an empty slot falls through to that flag's per-slot
 * auto-resolve default (e.g. `--price-decimals "8,,8"` overrides slots 0 and 2
 * and auto-resolves slot 1). Filtering empties would collapse the length and
 * either misalign the remaining entries or trip the length check.
 *
 * Alignment is validated only when the flag is PRESENT (`raw !== undefined`):
 * an unset flag yields `[]` and `mismatch: false`. A present flag whose entry
 * count ≠ `expectedLen` returns `mismatch: true` for the caller to hard-exit.
 * (An unset flag is `undefined` here; `""` — an explicitly empty flag — is
 * "present" and splits to `[""]`, i.e. one empty slot.)
 */
export function parseAlignedOverrides(
  raw: string | undefined,
  expectedLen: number,
): { values: string[]; mismatch: boolean } {
  if (raw === undefined) return { values: [], mismatch: false };
  const values = raw.split(",").map((s) => s.trim());
  return { values, mismatch: values.length !== expectedLen };
}

async function buildInitDataForTemplate(
  templateKey: string,
  opts: Record<string, string | boolean | undefined>,
  vault: Address,
): Promise<{ initData: Hex; asset: Address; assetAmount: bigint; extraApprovals?: { token: Address; amount: bigint }[] }> {
  if (templateKey === "moonwell-supply") {
    if (!opts.amount) {
      console.error(chalk.red("--amount is required for moonwell-supply template"));
      process.exit(1);
    }
    const token = (opts.token as string) || "USDC";
    const underlying = resolveToken(token);
    const mToken = resolveMToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    const supplyAmount = parseUnits(opts.amount as string, decimals);
    const minRedeem = parseUnits((opts.minRedeem as string) || opts.amount as string, decimals);
    // V2: Moonwell's native-ETH (mWETH) market redeems raw ETH the strategy
    // wraps back to WETH — every non-USDC token here is the WETH market.
    const isNativeEthMarket = token.toUpperCase() !== "USDC";

    return {
      initData: moonwellBuilder.buildInitData(underlying, mToken, supplyAmount, minRedeem, isNativeEthMarket),
      asset: underlying,
      assetAmount: supplyAmount,
    };
  }

  if (templateKey === "venice-inference") {
    if (!opts.amount) {
      console.error(chalk.red("--amount is required for venice-inference template"));
      process.exit(1);
    }
    const assetSymbol = (opts.asset as string) || "USDC";
    const asset = resolveToken(assetSymbol);
    const vvv = VENICE().VVV;
    const isDirect = asset.toLowerCase() === vvv.toLowerCase();
    const decimals = assetSymbol.toUpperCase() === "USDC" ? 6 : 18;
    const assetAmount = parseUnits(opts.amount as string, decimals);
    const agent = (opts.agent as Address) || getAccount().address;
    const isSingleHop = !!opts.singleHop;

    // Auto-quote minVVV if not specified (non-direct paths only)
    let minVVV = 0n;
    if (!isDirect) {
      if (opts.minVvv) {
        minVVV = parseUnits(opts.minVvv as string, 18);
      } else {
        // Quote expected VVV out and apply 5% slippage
        const publicClient = getPublicClient();
        const aeroRouterAbi = [{
          name: "getAmountsOut",
          type: "function",
          stateMutability: "view",
          inputs: [
            { name: "amountIn", type: "uint256" },
            { name: "routes", type: "tuple[]", components: [
              { name: "from", type: "address" },
              { name: "to", type: "address" },
              { name: "stable", type: "bool" },
              { name: "factory", type: "address" },
            ]},
          ],
          outputs: [{ name: "amounts", type: "uint256[]" }],
        }] as const;

        const routes = isSingleHop
          ? [{ from: asset, to: vvv, stable: false, factory: AERODROME().FACTORY }]
          : [
              { from: asset, to: TOKENS().WETH, stable: false, factory: AERODROME().FACTORY },
              { from: TOKENS().WETH, to: vvv, stable: false, factory: AERODROME().FACTORY },
            ];

        try {
          const amounts = await publicClient.readContract({
            address: AERODROME().ROUTER,
            abi: aeroRouterAbi,
            functionName: "getAmountsOut",
            args: [assetAmount, routes],
          });
          const expectedVVV = amounts[amounts.length - 1];
          // 5% slippage: minVVV = expectedVVV * 95 / 100
          minVVV = (expectedVVV * 95n) / 100n;
          console.log(chalk.dim(`  Auto-quoted minVVV: ${(Number(minVVV) / 1e18).toFixed(4)} VVV (5% slippage on ~${(Number(expectedVVV) / 1e18).toFixed(4)} VVV)`));
        } catch {
          console.error(chalk.red("Could not quote USDC→VVV price. Pass --min-vvv <amount> manually."));
          process.exit(1);
        }
      }
    }

    const params: veniceBuilder.VeniceInferenceInitParams = {
      asset,
      weth: isDirect ? ZERO : TOKENS().WETH,
      vvv,
      sVVV: VENICE().STAKING,
      aeroRouter: isDirect ? ZERO : AERODROME().ROUTER,
      aeroFactory: isDirect ? ZERO : AERODROME().FACTORY,
      agent,
      assetAmount,
      minVVV,
      deadlineOffset: 300n,
      singleHop: isSingleHop,
    };

    return {
      initData: veniceBuilder.buildInitData(params),
      asset,
      assetAmount,
    };
  }

  if (templateKey === "aerodrome-lp") {
    for (const flag of ["tokenA", "tokenB", "amountA", "amountB", "lpToken"]) {
      if (!opts[flag]) {
        console.error(chalk.red(`--${flag.replace(/([A-Z])/g, "-$1").toLowerCase()} is required for aerodrome-lp template`));
        process.exit(1);
      }
    }
    const tokenA = opts.tokenA as Address;
    const tokenB = opts.tokenB as Address;
    const publicClient = getPublicClient();
    const [decimalsA, decimalsB] = await Promise.all([
      publicClient.readContract({ address: tokenA, abi: erc20Abi, functionName: "decimals" }),
      publicClient.readContract({ address: tokenB, abi: erc20Abi, functionName: "decimals" }),
    ]);
    const amountA = parseUnits(opts.amountA as string, decimalsA);
    const amountB = parseUnits(opts.amountB as string, decimalsB);
    const minAOut = parseUnits((opts.minAOut as string) || "0", decimalsA);
    const minBOut = parseUnits((opts.minBOut as string) || "0", decimalsB);

    const params: aerodromeBuilder.AerodromeLPInitParams = {
      tokenA,
      tokenB,
      stable: !!opts.stable,
      factory: AERODROME().FACTORY,
      router: AERODROME().ROUTER,
      gauge: (opts.gauge as Address) || ZERO,
      lpToken: opts.lpToken as Address,
      amountADesired: amountA,
      amountBDesired: amountB,
      amountAMin: amountA, // use desired as min for now
      amountBMin: amountB,
      minAmountAOut: minAOut,
      minAmountBOut: minBOut,
    };

    return {
      initData: aerodromeBuilder.buildInitData(params),
      asset: tokenA,
      assetAmount: amountA,
      extraApprovals: [{ token: tokenB, amount: amountB }],
    };
  }

  if (templateKey === "wsteth-moonwell") {
    const supplyAmount = opts.amount ? parseUnits(opts.amount as string, 18) : 0n; // WETH = 18 decimals; 0 => use full vault balance at execute time
    const slippageBps = BigInt((opts.slippage as string) || "500"); // default 5% slippage
    // Per-unit rates (1e18-scaled) — Aerodrome wstETH/WETH stable pool trades
    // near 1:1, so default the expected rate to 1e18. Slippage cuts from that.
    // Rates scale with amountIn at execute time → dynamic-all mode is safe.
    const ONE = 10n ** 18n;
    const minWstethOutPerWeth = (ONE * (10000n - slippageBps)) / 10000n;
    const minWethOutPerWsteth = (ONE * (10000n - slippageBps)) / 10000n;

    const params: wstethBuilder.WstETHMoonwellInitParams = {
      weth: TOKENS().WETH,
      wsteth: TOKENS().wstETH,
      mwsteth: MOONWELL().mWstETH,
      aeroRouter: AERODROME().ROUTER,
      aeroFactory: AERODROME().FACTORY,
      chainlinkWstethEthFeed: CHAINLINK().WSTETH_ETH_FEED,
      supplyAmount,
      minWstethOutPerWeth,
      minWethOutPerWsteth,
      deadlineOffset: 300n,
    };

    return {
      initData: wstethBuilder.buildInitData(params),
      asset: TOKENS().WETH,
      assetAmount: supplyAmount,
    };
  }

  if (templateKey === "mamo-yield") {
    if (!opts.amount) {
      console.error(chalk.red("--amount is required for mamo-yield template"));
      process.exit(1);
    }
    if (!opts.mamoFactory) {
      console.error(chalk.red("--mamo-factory is required for mamo-yield template"));
      process.exit(1);
    }
    const token = (opts.token as string) || "USDC";
    const underlying = resolveToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    const amount = parseUnits(opts.amount as string, decimals);
    const minRedeemAmount = parseUnits((opts.minRedeem as string) || opts.amount as string, decimals);

    return {
      initData: mamoBuilder.buildInitData(underlying, opts.mamoFactory as Address, minRedeemAmount),
      asset: underlying,
      assetAmount: amount,
    };
  }

  if (templateKey === "portfolio") {
    if (!opts.amount) { console.error(chalk.red("--amount is required for portfolio template")); process.exit(1); }
    if (!opts.tokens || !opts.weights) {
      console.error(chalk.red("--tokens and --weights are required for portfolio template"));
      console.error(chalk.dim("  --tokens: comma-separated token addresses or symbols"));
      console.error(chalk.dim("  --weights: comma-separated bps (must sum to 10000)"));
      process.exit(1);
    }
    const network = getNetwork();
    const tokens = TOKENS();
    const assetSymbol = (opts.asset as string) || defaultPortfolioAsset();
    const asset = resolveToken(assetSymbol);
    const decimals = assetDecimalsForSymbol(assetSymbol);
    const totalAmount = parseUnits(opts.amount as string, decimals);
    const maxSlippageBps = Number((opts.maxSlippage as string) || "500");
    const feeTier = (opts.feeTier as string) || "3000";

    // Parse token specs, keeping the original symbol/spec so we can resolve a
    // Chainlink push feed on chains that use AggregatorV3 push feeds.
    const tokenSpecs = (opts.tokens as string).split(",").map((t) => t.trim());
    const tokenAddrs = tokenSpecs.map((trimmed) => {
      if (isAddress(trimmed)) return trimmed as Address;
      const allTokens = tokens as Record<string, Address>;
      const resolved = allTokens[trimmed.toUpperCase()];
      if (resolved && resolved !== ZERO) return resolved;
      console.error(chalk.red(`Unknown token: ${trimmed}`)); process.exit(1);
    });
    const weightsBps = (opts.weights as string).split(",").map((w) => Number(w.trim()));
    if (tokenAddrs.length !== weightsBps.length) { console.error(chalk.red("--tokens and --weights must have same length")); process.exit(1); }
    if (weightsBps.reduce((a, b) => a + b, 0) !== 10000) { console.error(chalk.red(`Weights must sum to 10000`)); process.exit(1); }

    const swapAdapter = (opts.swapAdapter as Address) || resolveSwapAdapter();
    const chainlinkVerifier = CHAINLINK().VERIFIER_PROXY;
    // Push-feed mode (chainlinkVerifier == 0, e.g. Robinhood mainnet): each
    // allocation's feedId is an AggregatorV3 proxy left-padded to bytes32, and
    // priceDecimals must equal that feed's decimals() (8 on Robinhood mainnet).
    const pushMode = chainlinkVerifier === ZERO;
    const chainlink = CHAINLINK() as Record<string, Address>;

    // Optional explicit per-allocation overrides (comma-separated, aligned to
    // --tokens). All four flags parse identically via parseAlignedOverrides:
    // empty entries are preserved so a partial override (e.g. "8,,8") falls
    // through to that slot's auto-resolve default; a PRESENT flag must align
    // 1:1 with --tokens (an unset flag → [] and skips the check).
    //   --feed-ids:       bytes32 feed ids (Data Streams) or feed addresses (push
    //                     mode). Empty slot → auto-resolve SYMBOL_USD_FEED below.
    //   --price-decimals: raw feed scale per allocation. Empty slot → pushMode 8
    //                     / else 18 default below.
    const feedIds = parseAlignedOverrides(opts.feedIds as string | undefined, tokenAddrs.length);
    const feedIdOverrides = feedIds.values;
    if (feedIds.mismatch) { console.error(chalk.red("--feed-ids must have the same length as --tokens")); process.exit(1); }
    const priceDecimals = parseAlignedOverrides(opts.priceDecimals as string | undefined, tokenAddrs.length);
    const priceDecimalOverrides = priceDecimals.values;
    if (priceDecimals.mismatch) { console.error(chalk.red("--price-decimals must have the same length as --tokens")); process.exit(1); }

    // Explicit per-token swap-route overrides (aligned to --tokens). An empty
    // entry falls through to the robinhood default / auto-detect below.
    const swapRoutes = parseAlignedOverrides(opts.swapRoutes as string | undefined, tokenAddrs.length);
    const swapRouteOverrides = swapRoutes.values;
    if (swapRoutes.mismatch) { console.error(chalk.red("--swap-routes must have the same length as --tokens")); process.exit(1); }

    // Optional per-token max-price-age override (seconds), comma-aligned to
    // --tokens. Packs into the push-mode feedId's upper bits. Empty entry → the
    // registry-driven default (96h for equity feeds, 0/contract-default otherwise).
    const maxPriceAges = parseAlignedOverrides(opts.maxPriceAges as string | undefined, tokenAddrs.length);
    const maxPriceAgeOverrides = maxPriceAges.values;
    if (maxPriceAges.mismatch) { console.error(chalk.red("--max-price-ages must have the same length as --tokens")); process.exit(1); }

    // Robinhood mainnet default swap routes come from the data-driven
    // ROBINHOOD_STOCK_ROUTES registry (verified on-chain liquidity) plus a WETH
    // v3 fee-500 leg. A robinhood basket token with a feed but NO verified route
    // and NO --swap-routes override fails fast (see below) rather than falling
    // through to a v3 auto-detect that is guaranteed dead on 4663.
    const wethAddr = TOKENS().WETH;
    function robinhoodDefaultRoute(token: Address, symbol: string): { extraData: Hex; routeDesc: string } | null {
      // Robinhood-mainnet-only default route (verified ROBINHOOD_STOCK_ROUTES).
      // Mainnet is not in the current deployment target, so this is inert today;
      // string-compare keeps it forward-compatible for when 4663 is added back.
      if ((network as string) !== "robinhood") return null;
      if (wethAddr !== ZERO && token.toLowerCase() === wethAddr.toLowerCase()) {
        return { extraData: portfolioBuilder.v3SingleHopExtraData(500), routeDesc: "v3 direct (fee 500)" };
      }
      const r = ROBINHOOD_STOCK_ROUTES[symbol.toUpperCase()];
      if (r) {
        return {
          extraData: portfolioBuilder.v4PathExtraData([
            { currency: ZERO, fee: r.nativeFee, tickSpacing: r.nativeTickSpacing },
            { currency: token, fee: r.stockFee, tickSpacing: r.stockTickSpacing },
          ]),
          routeDesc: `v4 multi-hop (USDG→ETH fee ${r.nativeFee} / ts ${r.nativeTickSpacing} → ${symbol.toUpperCase()} fee ${r.stockFee} / ts ${r.stockTickSpacing})`,
        };
      }
      return null;
    }

    // Auto-detect swap routes for each token
    console.log(chalk.dim("  Detecting swap routes..."));
    const allocations: portfolioBuilder.BasketAllocation[] = [];
    for (let i = 0; i < tokenAddrs.length; i++) {
      const token = tokenAddrs[i];
      let route: { extraData: Hex; routeDesc: string } | null = null;
      const override = swapRouteOverrides[i];
      if (override) {
        try {
          route = { extraData: parseSwapRouteSpec(override, token), routeDesc: `explicit '${override}'` };
        } catch (e) {
          console.error(chalk.red(`Bad --swap-routes entry for ${tokenSpecs[i]}: ${(e as Error).message}`));
          process.exit(1);
        }
      }
      if (!route) route = robinhoodDefaultRoute(token, tokenSpecs[i]);
      if (!route && (network as string) === "robinhood") {
        // No verified default route and no override. v3 auto-detect is dead on
        // 4663 (no v3 stock pools) — fail fast with an actionable message rather
        // than emitting a doomed proposal.
        console.error(
          chalk.red(
            `No verified default swap route for ${tokenSpecs[i]} on robinhood mainnet.`,
          ),
        );
        console.error(
          chalk.yellow(
            `  Pass --swap-routes to set it explicitly for this token, e.g.\n` +
              `    --swap-routes 'v4path:eth@500:10+self@50000:1000'  (USDG→ETH→${tokenSpecs[i].toUpperCase()})`,
          ),
        );
        process.exit(1);
      }
      if (!route) route = await detectSwapRoute(asset, token, Number(feeTier));
      if (!route) {
        console.error(chalk.red(`No swap route found for ${token}. No Uniswap V3 pool (direct or via WETH). Pass --swap-routes to set it explicitly.`));
        process.exit(1);
      }
      console.log(chalk.dim(`    ${token.slice(0, 10)}... → ${route.routeDesc}`));

      // Resolve the per-allocation Chainlink feed id + priceDecimals.
      let feedId = feedIdOverrides[i] as portfolioBuilder.BasketAllocation["feedId"] | undefined;
      // Default priceDecimals: 8 in push mode (Robinhood mainnet feeds),
      // 18 otherwise (crypto Data Streams pairs); override wins.
      let priceDecimals = priceDecimalOverrides[i]
        ? Number(priceDecimalOverrides[i])
        : pushMode ? 8 : 18;

      // Per-slot max price age (seconds), packed into the push-mode feedId.
      // Precedence: explicit --max-price-ages > registry default (96h for
      // Robinhood equity feeds; 0 = contract default for crypto/stable feeds).
      const sym = tokenSpecs[i].toUpperCase();
      let maxPriceAge = 0;
      if (maxPriceAgeOverrides[i]) {
        // Explicit --max-price-ages overrides everything (unchanged precedence).
        maxPriceAge = Number(maxPriceAgeOverrides[i]);
        if (!Number.isInteger(maxPriceAge) || maxPriceAge < 0) {
          console.error(chalk.red(`Bad --max-price-ages entry for ${tokenSpecs[i]}: '${maxPriceAgeOverrides[i]}' (expected a non-negative integer count of seconds)`));
          process.exit(1);
        }
      } else if (pushMode && (network as string) === "robinhood") {
        // Registry-driven default: classify the token as equity vs crypto to
        // pack the 96h max-age into the push feedId. Works for tokens given by
        // SYMBOL and (via reverse-lookup) by raw 0x address — otherwise a stock
        // token passed by address silently falls back to the contract's 26h
        // default and trips StalePrice over long weekends.
        const { maxPriceAge: age, unknownAddress } = robinhoodEquityMaxAge(
          tokenSpecs[i],
          tokens as Record<string, Address>,
          portfolioBuilder.EQUITY_MAX_PRICE_AGE_SECONDS,
        );
        maxPriceAge = age;
        if (unknownAddress && feedIdOverrides[i]) {
          // Raw address unknown to the registry + an explicit feed id: we can't
          // classify it, so the slot will use the contract's 26h default. Warn
          // rather than guess — equity feeds need an explicit --max-price-ages.
          console.warn(
            chalk.yellow(
              `  Warning: token ${tokenSpecs[i]} is not in the ${network} registry, so its ` +
                `push feed uses the contract's 26h default max-price-age.\n` +
                `  If this is a tokenized-equity feed, pass --max-price-ages (e.g. ${portfolioBuilder.EQUITY_MAX_PRICE_AGE_SECONDS}) ` +
                `for this slot or it will trip StalePrice over long weekends.`,
            ),
          );
        }
      }

      if (pushMode && !feedId) {
        // Auto-resolve SYMBOL_USD_FEED from the CHAINLINK() registry by symbol.
        // WETH/ETH share the ETH_USD_FEED key (there is no WETH_USD_FEED).
        const feedKey = sym === "WETH" || sym === "ETH" ? "ETH_USD_FEED" : `${sym}_USD_FEED`;
        const feed = isAddress(tokenSpecs[i]) ? undefined : chainlink[feedKey];
        if (!feed || feed === ZERO) {
          console.error(chalk.red(`No Chainlink push feed for ${tokenSpecs[i]} on ${network}. Pass --feed-ids <feedAddr,...> to supply AggregatorV3 addresses.`));
          process.exit(1);
        }
        feedId = portfolioBuilder.pushFeedId(feed, maxPriceAge);
      } else if (pushMode && feedId && isAddress(feedId)) {
        // Push-mode override given as a bare address — pad to bytes32 (+ age).
        feedId = portfolioBuilder.pushFeedId(feedId as Address, maxPriceAge);
      }

      if (!feedId || feedId === ZERO_BYTES32) {
        console.error(chalk.red(`Missing Chainlink feedId for allocation ${i} (${tokenSpecs[i]}). The strategy requires a non-zero feedId per token — pass --feed-ids as a comma-separated list.`));
        process.exit(1);
      }

      allocations.push({
        token, weightBps: weightsBps[i],
        swapExtraData: route.extraData,
        priceDecimals,
        feedId,
      });
    }
    return {
      initData: portfolioBuilder.buildInitData(asset, swapAdapter, chainlinkVerifier, allocations, totalAmount, maxSlippageBps),
      asset, assetAmount: totalAmount,
    };
  }

  if (templateKey === "hyperliquid-perp") {
    const token = (opts.token as string) || "USDC";
    const asset = resolveToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    // Omit --amount to use the vault's full asset balance at execute time (dynamic-all mode).
    const depositAmount = opts.amount ? parseUnits(opts.amount as string, decimals) : 0n;
    const leverage = Number((opts.leverage as string) || "10");
    const assetIndex = Number((opts.assetIndex as string) || "0");
    const maxPosition = parseUnits((opts.maxPosition as string) || "100000", decimals);
    const maxTradesDay = Number((opts.maxTradesPerDay as string) || "50");
    return {
      initData: hyperliquidPerpBuilder.buildInitData(asset, depositAmount, assetIndex, leverage, maxPosition, maxTradesDay),
      asset, assetAmount: depositAmount,
    };
  }

  if (templateKey === "hyperliquid-grid") {
    const token = (opts.token as string) || "USDC";
    const asset = resolveToken(token);
    const decimals = token.toUpperCase() === "USDC" ? 6 : 18;
    const depositAmount = opts.amount ? parseUnits(opts.amount as string, decimals) : 0n;
    const leverage = Number((opts.leverage as string) || "5");
    const maxOrderSize = parseUnits((opts.maxOrderSize as string) || "10000", decimals);
    const maxOrdersPerTick = Number((opts.maxOrdersPerTick as string) || "20");
    const assetIndices = ((opts.assetIndices as string) || "0")
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => !Number.isNaN(n));
    return {
      initData: hyperliquidGridBuilder.buildInitData(asset, depositAmount, leverage, maxOrderSize, maxOrdersPerTick, assetIndices),
      asset, assetAmount: depositAmount,
    };
  }

  throw new Error(`No init builder for template: ${templateKey}`);
}

function buildCallsForTemplate(
  templateKey: string,
  clone: Address,
  asset: Address,
  assetAmount: bigint,
  extraApprovals?: { token: Address; amount: bigint }[],
): { executeCalls: BatchCall[]; settleCalls: BatchCall[] } {
  if (templateKey === "moonwell-supply") {
    return {
      executeCalls: moonwellBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: moonwellBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "venice-inference") {
    return {
      executeCalls: veniceBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: veniceBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "aerodrome-lp") {
    const tokenB = extraApprovals?.[0]?.token ?? ZERO;
    const amountB = extraApprovals?.[0]?.amount ?? 0n;
    return {
      executeCalls: aerodromeBuilder.buildExecuteCalls(clone, asset, assetAmount, tokenB, amountB),
      settleCalls: aerodromeBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "wsteth-moonwell") {
    return {
      executeCalls: wstethBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: wstethBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "mamo-yield") {
    return {
      executeCalls: mamoBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: mamoBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "portfolio") {
    return {
      executeCalls: portfolioBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: portfolioBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "hyperliquid-perp") {
    return {
      executeCalls: hyperliquidPerpBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: hyperliquidPerpBuilder.buildSettleCalls(clone),
    };
  }

  if (templateKey === "hyperliquid-grid") {
    return {
      executeCalls: hyperliquidGridBuilder.buildExecuteCalls(clone, asset, assetAmount),
      settleCalls: hyperliquidGridBuilder.buildSettleCalls(clone),
    };
  }

  throw new Error(`No call builder for template: ${templateKey}`);
}

// ── Token resolution ──

function resolveSwapAdapter(): Address {
  const network = getNetwork();
  if (network === "robinhood-testnet" && SYNTHRA().ROUTER === ZERO) {
    console.error(chalk.red("Synthra DEX not available"));
    process.exit(1);
  }
  // The deployed adapter on robinhood-testnet is the registry's UniswapSwapAdapter
  // (UNISWAP_SWAP_ADAPTER), backed by Synthra's router + a QuoterV2 shim
  // (SYNTHRA_QUOTER_V2_SHIM). It consumes MODE-PREFIXED extraData (0x00 single-hop
  // / 0x01 multi-hop — see buildSwapExtraData). An earlier build hardcoded a bare
  // SynthraDirectAdapter address here paired with un-prefixed extraData; that
  // adapter's on-chain `quote()` returns 0 (bricking PortfolioStrategy execute
  // with QuoteUnavailable), so always resolve the registry adapter instead.
  const adapterAddr = UNISWAP().SWAP_ADAPTER;
  if (adapterAddr !== ZERO) return adapterAddr;
  console.error(chalk.red("UniswapSwapAdapter not deployed yet. Use --swap-adapter to specify manually."));
  process.exit(1);
}

function resolveToken(symbolOrAddress: string): Address {
  if (isAddress(symbolOrAddress)) return symbolOrAddress as Address;
  const upper = symbolOrAddress.toUpperCase();
  // Spread the per-network TOKENS() registry (so USDG / stock tokens on robinhood
  // resolve without a second hand-rolled map — mirrors the basket-token path),
  // then overlay VVV which lives in the Venice registry rather than TOKENS().
  const tokenMap: Record<string, Address> = {
    ...(TOKENS() as Record<string, Address>),
    VVV: VENICE().VVV,
  };
  const addr = tokenMap[upper];
  if (!addr || addr === ZERO) {
    console.error(chalk.red(`Unknown token: ${symbolOrAddress}`));
    process.exit(1);
  }
  return addr;
}

/**
 * Default vault asset for the portfolio template on the current network:
 * WETH on Robinhood testnet (ETH-only chain, the current deployment target),
 * USDC as the forward-compatible default for any other chain added back. Single
 * source of truth shared by buildInitDataForTemplate + computeProposalAssetChecks.
 */
function defaultPortfolioAsset(): string {
  const network = getNetwork();
  if (network === "robinhood-testnet") return "WETH";
  return "USDC";
}

/**
 * Decimals for a vault-asset symbol. USDC and USDG are 6-decimal stables; every
 * other supported vault asset (WETH, stock tokens, …) is 18-decimal. Kept as one
 * helper so the funding math can't drift between the preflight check and the
 * on-chain init data.
 */
function assetDecimalsForSymbol(symbol: string): number {
  const upper = symbol.toUpperCase();
  return upper === "USDC" || upper === "USDG" ? 6 : 18;
}

function resolveMToken(tokenSymbol: string): Address {
  const upper = tokenSymbol.toUpperCase();
  const moonwell = MOONWELL();
  const mTokenMap: Record<string, Address> = {
    USDC: moonwell.mUSDC,
    WETH: moonwell.mWETH,
  };
  const addr = mTokenMap[upper];
  if (!addr || addr === ZERO) {
    console.error(chalk.red(`No Moonwell market for: ${tokenSymbol}`));
    process.exit(1);
  }
  return addr;
}

function serializeCalls(calls: BatchCall[]): string {
  return JSON.stringify(
    calls.map((c) => ({
      target: c.target,
      data: c.data,
      value: c.value.toString(),
    })),
    null,
    2,
  );
}

// ── Commands ──

export function registerStrategyTemplateCommands(strategy: Command): void {
  // ── strategy list ──

  strategy
    .command("list")
    .description("List available strategy templates")
    .action(() => {
      const templates = STRATEGY_TEMPLATES();
      const network = getNetwork();

      console.log();
      console.log(chalk.bold("Strategy Templates"), chalk.dim(`(${network})`));
      console.log(chalk.dim("─".repeat(60)));

      let availableCount = 0;
      for (const t of TEMPLATES) {
        const addr = templates[t.addressKey];
        if (addr !== ZERO) {
          availableCount++;
          console.log();
          console.log(`  ${chalk.bold(t.name)} (${chalk.cyan(t.key)})`);
          console.log(`    ${t.description}`);
          console.log(`    Template: ${chalk.green(addr)}`);
        }
      }

      if (availableCount === 0) {
        console.log();
        console.log(chalk.yellow("  No strategy templates deployed on this network."));
      }

      const unavailable = TEMPLATES.filter((t) => templates[t.addressKey] === ZERO);
      if (unavailable.length > 0) {
        console.log();
        console.log(chalk.dim(`  Not available on ${network}:`));
        for (const t of unavailable) {
          console.log(chalk.dim(`    ${t.name} (${t.key})`));
        }
      }

      console.log();
      console.log(chalk.dim("Clone a template:  sherwood strategy clone <template> --vault <addr> ..."));
      console.log(chalk.dim("Full proposal:     sherwood strategy propose <template> --vault <addr> ..."));
      console.log();
    });

  // ── strategy clone ──

  strategy
    .command("clone")
    .description("Clone a strategy template and initialize it")
    .argument("<template>", "Template: moonwell-supply, aerodrome-lp, venice-inference, wsteth-moonwell, mamo-yield, portfolio, hyperliquid-perp, hyperliquid-grid")
    .requiredOption("--vault <address>", "Vault address")
    // moonwell-supply / wsteth-moonwell
    .option("--amount <n>", "Asset amount to deploy")
    .option("--min-redeem <n>", "Min asset on settlement (Moonwell)")
    .option("--token <symbol>", "Asset token symbol (default: USDC)")
    // venice-inference
    .option("--asset <symbol>", "Asset token (USDC, VVV, or address)")
    .option("--agent <address>", "Agent wallet (Venice, default: your wallet)")
    .option("--min-vvv <n>", "Min VVV from swap (Venice)")
    .option("--single-hop", "Single-hop Aerodrome swap (Venice)")
    // aerodrome-lp
    .option("--token-a <address>", "Token A (Aerodrome)")
    .option("--token-b <address>", "Token B (Aerodrome)")
    .option("--amount-a <n>", "Token A amount (Aerodrome)")
    .option("--amount-b <n>", "Token B amount (Aerodrome)")
    .option("--stable", "Stable pool (Aerodrome)")
    .option("--gauge <address>", "Gauge address (Aerodrome)")
    .option("--lp-token <address>", "LP token address (Aerodrome)")
    .option("--min-a-out <n>", "Min token A on settle (Aerodrome)")
    .option("--min-b-out <n>", "Min token B on settle (Aerodrome)")
    // wsteth-moonwell
    .option("--slippage <bps>", "Slippage tolerance in bps (wstETH, default: 500 = 5%)")
    // mamo-yield
    .option("--mamo-factory <address>", "Mamo StrategyFactory address (Mamo)")
    // portfolio
    .option("--tokens <list>", "Comma-separated token addresses or symbols (Portfolio)")
    .option("--weights <list>", "Comma-separated weights in bps, must sum to 10000 (Portfolio)")
    .option("--max-slippage <bps>", "Max slippage bps (Portfolio, default: 500)")
    .option("--fee-tier <n>", "Pool fee tier (Portfolio, default: 3000)")
    .option("--swap-adapter <address>", "Swap adapter address (Portfolio)")
    .option("--feed-ids <list>", "Comma-separated Chainlink feed ids/addresses per token (Portfolio; auto-resolved from registry in push-feed mode)")
    .option("--price-decimals <list>", "Comma-separated raw feed decimals per token (Portfolio; default 8 push / 18 Data Streams)")
    .option("--swap-routes <list>", "Comma-separated per-token swap route spec (Portfolio). Per token: 'v3:<fee>' (mode 0), 'v4:<fee>:<tickSpacing>' (mode 2), or 'v4path:<hop>+<hop>...' (mode 3) where hop='<currency>@<fee>:<tickSpacing>', currency='eth'/'native'=ETH or 'self'=this token or an address. Empty entry = auto-detect / robinhood default.")
    .option("--max-price-ages <list>", "Comma-separated per-token max Chainlink push-feed staleness in seconds (Portfolio, push mode; robinhood-mainnet equity feeds default to 345600 = 96h, crypto/stable feeds to the contract default). Empty entry = registry default.")
    // hyperliquid-perp
    .option("--leverage <number>", "Leverage multiplier (Hyperliquid Perp, default: 10)")
    .option("--asset-index <number>", "Perp asset index (Hyperliquid Perp, default: 0 for BTC)")
    .option("--max-position <amount>", "Max position size in USD (Hyperliquid Perp, default: 100000)")
    .option("--max-trades-per-day <n>", "Max trades per day (Hyperliquid Perp, default: 50)")
    .option("--max-order-size <amount>", "Max single-order size in asset units (Hyperliquid Grid, default: 10000)")
    .option("--max-orders-per-tick <n>", "Max orders per tick (Hyperliquid Grid, default: 20)")
    .option("--asset-indices <list>", "Comma-separated perp asset indices (Hyperliquid Grid, default: 0)")
    .action(async (templateKey: string, opts) => {
      const vault = opts.vault as Address;
      if (!isAddress(vault)) {
        console.error(chalk.red("Invalid vault address"));
        process.exit(1);
      }

      const { def, address: templateAddr } = resolveTemplate(templateKey);

      // 1. Clone
      const cloneSpinner = ora(`Cloning ${def.name} template...`).start();
      let clone: Address;
      let cloneHash: Hex;
      try {
        const result = await cloneTemplate(templateAddr);
        clone = result.clone;
        cloneHash = result.hash;
        cloneSpinner.succeed(`Cloned: ${chalk.green(clone)}`);
        console.log(chalk.dim(`  Tx: ${getExplorerUrl(cloneHash)}`));
      } catch (err) {
        cloneSpinner.fail("Clone failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // 2. Initialize
      const initSpinner = ora("Initializing strategy...").start();
      try {
        const { initData } = await buildInitDataForTemplate(templateKey, opts, vault);
        const account = getAccount();

        const initHash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: clone,
          abi: BASE_STRATEGY_ABI,
          functionName: "initialize",
          args: [vault, account.address, initData],
        });

        const receipt = await waitForReceipt(initHash);
        if (receipt.status === "reverted") {
          throw new Error("Initialize transaction reverted on-chain");
        }
        initSpinner.succeed("Initialized");
      } catch (err) {
        initSpinner.fail("Initialize failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // 3. HyperCore registration (grid + perp)
      // BaseStrategy.initialize() writes address(this) to slot 0 (_hcSelf). HC
      // reads slot 0 post-block via FirstStorageSlot variant — no nonce
      // derivation needed.
      if (templateKey === "hyperliquid-grid" || templateKey === "hyperliquid-perp") {
        const hcSpinner = ora("Registering clone with HyperCore...").start();
        try {
          const account = getAccount();
          const hcHash = await writeContractWithRetry({
            account,
            chain: getChain(),
            address: clone,
            abi: templateKey === "hyperliquid-grid" ? HYPERLIQUID_GRID_STRATEGY_ABI : HYPERLIQUID_PERP_STRATEGY_ABI,
            functionName: "finalizeForHyperCore",
            args: [0n, 1, 0n],  // token=USDC, variant=FirstStorageSlot(1), nonce unused
          });
          await waitForReceipt(hcHash);
          hcSpinner.succeed("HyperCore registration sent (active after next block)");
        } catch (err) {
          hcSpinner.warn("HyperCore registration failed — run finalizeForHyperCore manually");
          console.error(chalk.yellow(formatContractError(err)));
        }
      }

      console.log();
      console.log(chalk.bold("Strategy clone ready:"), chalk.green(clone));
      console.log(chalk.dim("Use this address in your proposal batch calls."));
      console.log();
    });

  // ── strategy init ──

  strategy
    .command("init")
    .description("Initialize an already-deployed but uninitialized strategy clone")
    .argument("<template>", "Template: moonwell-supply, aerodrome-lp, venice-inference, wsteth-moonwell, mamo-yield, portfolio, hyperliquid-perp, hyperliquid-grid")
    .requiredOption("--clone <address>", "Clone address to initialize")
    .requiredOption("--vault <address>", "Vault address")
    // moonwell-supply / wsteth-moonwell
    .option("--amount <n>", "Asset amount to deploy")
    .option("--min-redeem <n>", "Min asset on settlement (Moonwell)")
    .option("--token <symbol>", "Asset token symbol (default: USDC)")
    // venice-inference
    .option("--asset <symbol>", "Asset token (USDC, VVV, or address)")
    .option("--agent <address>", "Agent wallet (Venice, default: your wallet)")
    .option("--min-vvv <n>", "Min VVV from swap (Venice)")
    .option("--single-hop", "Single-hop Aerodrome swap (Venice)")
    // aerodrome-lp
    .option("--token-a <address>", "Token A (Aerodrome)")
    .option("--token-b <address>", "Token B (Aerodrome)")
    .option("--amount-a <n>", "Token A amount (Aerodrome)")
    .option("--amount-b <n>", "Token B amount (Aerodrome)")
    .option("--stable", "Stable pool (Aerodrome)")
    .option("--gauge <address>", "Gauge address (Aerodrome)")
    .option("--lp-token <address>", "LP token address (Aerodrome)")
    .option("--min-a-out <n>", "Min token A on settle (Aerodrome)")
    .option("--min-b-out <n>", "Min token B on settle (Aerodrome)")
    // wsteth-moonwell
    .option("--slippage <bps>", "Slippage tolerance in bps (wstETH, default: 500 = 5%)")
    // mamo-yield
    .option("--mamo-factory <address>", "Mamo StrategyFactory address (Mamo)")
    // portfolio
    .option("--tokens <list>", "Comma-separated token addresses or symbols (Portfolio)")
    .option("--weights <list>", "Comma-separated weights in bps, must sum to 10000 (Portfolio)")
    .option("--max-slippage <bps>", "Max slippage bps (Portfolio, default: 500)")
    .option("--fee-tier <n>", "Pool fee tier (Portfolio, default: 3000)")
    .option("--swap-adapter <address>", "Swap adapter address (Portfolio)")
    .option("--feed-ids <list>", "Comma-separated Chainlink feed ids/addresses per token (Portfolio; auto-resolved from registry in push-feed mode)")
    .option("--price-decimals <list>", "Comma-separated raw feed decimals per token (Portfolio; default 8 push / 18 Data Streams)")
    .option("--swap-routes <list>", "Comma-separated per-token swap route spec (Portfolio). Per token: 'v3:<fee>' (mode 0), 'v4:<fee>:<tickSpacing>' (mode 2), or 'v4path:<hop>+<hop>...' (mode 3) where hop='<currency>@<fee>:<tickSpacing>', currency='eth'/'native'=ETH or 'self'=this token or an address. Empty entry = auto-detect / robinhood default.")
    .option("--max-price-ages <list>", "Comma-separated per-token max Chainlink push-feed staleness in seconds (Portfolio, push mode; robinhood-mainnet equity feeds default to 345600 = 96h, crypto/stable feeds to the contract default). Empty entry = registry default.")
    // hyperliquid-perp
    .option("--leverage <number>", "Leverage multiplier (Hyperliquid Perp, default: 10)")
    .option("--asset-index <number>", "Perp asset index (Hyperliquid Perp, default: 0 for BTC)")
    .option("--max-position <amount>", "Max position size in USD (Hyperliquid Perp, default: 100000)")
    .option("--max-trades-per-day <n>", "Max trades per day (Hyperliquid Perp, default: 50)")
    .option("--max-order-size <amount>", "Max single-order size in asset units (Hyperliquid Grid, default: 10000)")
    .option("--max-orders-per-tick <n>", "Max orders per tick (Hyperliquid Grid, default: 20)")
    .option("--asset-indices <list>", "Comma-separated perp asset indices (Hyperliquid Grid, default: 0)")
    .action(async (templateKey: string, opts) => {
      const clone = opts.clone as Address;
      const vault = opts.vault as Address;
      if (!isAddress(clone)) {
        console.error(chalk.red("Invalid clone address"));
        process.exit(1);
      }
      if (!isAddress(vault)) {
        console.error(chalk.red("Invalid vault address"));
        process.exit(1);
      }

      resolveTemplate(templateKey); // validate template exists

      // Check if already initialized
      const publicClient = getPublicClient();
      const currentVault = await publicClient.readContract({
        address: clone,
        abi: BASE_STRATEGY_ABI,
        functionName: "vault",
      }) as Address;

      if (currentVault !== "0x0000000000000000000000000000000000000000") {
        console.error(chalk.red(`Clone already initialized (vault: ${currentVault})`));
        process.exit(1);
      }

      const initSpinner = ora("Initializing strategy clone...").start();
      try {
        const { initData } = await buildInitDataForTemplate(templateKey, opts, vault);
        const account = getAccount();

        const initHash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: clone,
          abi: BASE_STRATEGY_ABI,
          functionName: "initialize",
          args: [vault, account.address, initData],
        });

        const receipt = await waitForReceipt(initHash);
        if (receipt.status === "reverted") {
          throw new Error("Initialize transaction reverted on-chain");
        }
        initSpinner.succeed("Initialized");
        console.log(chalk.dim(`  Tx: ${getExplorerUrl(initHash)}`));
      } catch (err) {
        initSpinner.fail("Initialize failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // Verify
      const verifiedVault = await publicClient.readContract({
        address: clone,
        abi: BASE_STRATEGY_ABI,
        functionName: "vault",
      }) as Address;

      console.log();
      console.log(chalk.bold("Clone initialized:"), chalk.green(clone));
      console.log(`  Vault:    ${chalk.green(verifiedVault)}`);
      console.log(`  Proposer: ${chalk.green(getAccount().address)}`);
      console.log();
    });

  // ── strategy propose ──

  strategy
    .command("propose")
    .description("Clone + init + build calls + submit governance proposal (all-in-one)")
    .argument("<template>", "Template: moonwell-supply, aerodrome-lp, venice-inference, wsteth-moonwell, mamo-yield, portfolio, hyperliquid-perp, hyperliquid-grid")
    .requiredOption("--vault <address>", "Vault address")
    .option("--write-calls <dir>", "Write execute/settle JSON to directory (skip proposal submission)")
    // proposal metadata (required unless --write-calls)
    .option("--name <name>", "Proposal name")
    .option("--description <text>", "Proposal description")
    .option("--duration <duration>", "Strategy duration (7d, 24h, etc.)")
    // external-signer (--calldata-only) flow
    .option("--proposer <address>", "Proposer/agent address (required for --calldata-only — the external signer)")
    .option("--metadata-uri <uri>", "Metadata URI (required for --calldata-only — IPFS pin needs a signer)")
    .option("--salt <bytes32>", "CREATE2 salt for the deterministic clone (--calldata-only; default: derived from inputs)")
    // template-specific (same as clone)
    .option("--amount <n>", "Asset amount to deploy")
    .option("--min-redeem <n>", "Min asset on settlement (Moonwell)")
    .option("--token <symbol>", "Asset token symbol (default: USDC)")
    .option("--asset <symbol>", "Asset token (USDC, VVV, or address)")
    .option("--agent <address>", "Agent wallet (Venice, default: your wallet)")
    .option("--min-vvv <n>", "Min VVV from swap (Venice)")
    .option("--single-hop", "Single-hop Aerodrome swap (Venice)")
    .option("--token-a <address>", "Token A (Aerodrome)")
    .option("--token-b <address>", "Token B (Aerodrome)")
    .option("--amount-a <n>", "Token A amount (Aerodrome)")
    .option("--amount-b <n>", "Token B amount (Aerodrome)")
    .option("--stable", "Stable pool (Aerodrome)")
    .option("--gauge <address>", "Gauge address (Aerodrome)")
    .option("--lp-token <address>", "LP token address (Aerodrome)")
    .option("--min-a-out <n>", "Min token A on settle (Aerodrome)")
    .option("--min-b-out <n>", "Min token B on settle (Aerodrome)")
    // wsteth-moonwell
    .option("--slippage <bps>", "Slippage tolerance in bps (wstETH, default: 500 = 5%)")
    // mamo-yield
    .option("--mamo-factory <address>", "Mamo StrategyFactory address (Mamo)")
    // portfolio
    .option("--tokens <list>", "Comma-separated token addresses or symbols (Portfolio)")
    .option("--weights <list>", "Comma-separated weights in bps, must sum to 10000 (Portfolio)")
    .option("--max-slippage <bps>", "Max slippage bps (Portfolio, default: 500)")
    .option("--fee-tier <n>", "Pool fee tier (Portfolio, default: 3000)")
    .option("--swap-adapter <address>", "Swap adapter address (Portfolio)")
    .option("--feed-ids <list>", "Comma-separated Chainlink feed ids/addresses per token (Portfolio; auto-resolved from registry in push-feed mode)")
    .option("--price-decimals <list>", "Comma-separated raw feed decimals per token (Portfolio; default 8 push / 18 Data Streams)")
    .option("--swap-routes <list>", "Comma-separated per-token swap route spec (Portfolio). Per token: 'v3:<fee>' (mode 0), 'v4:<fee>:<tickSpacing>' (mode 2), or 'v4path:<hop>+<hop>...' (mode 3) where hop='<currency>@<fee>:<tickSpacing>', currency='eth'/'native'=ETH or 'self'=this token or an address. Empty entry = auto-detect / robinhood default.")
    .option("--max-price-ages <list>", "Comma-separated per-token max Chainlink push-feed staleness in seconds (Portfolio, push mode; robinhood-mainnet equity feeds default to 345600 = 96h, crypto/stable feeds to the contract default). Empty entry = registry default.")
    // hyperliquid-perp
    .option("--leverage <number>", "Leverage multiplier (Hyperliquid Perp, default: 10)")
    .option("--asset-index <number>", "Perp asset index (Hyperliquid Perp, default: 0 for BTC)")
    .option("--max-position <amount>", "Max position size in USD (Hyperliquid Perp, default: 100000)")
    .option("--max-trades-per-day <n>", "Max trades per day (Hyperliquid Perp, default: 50)")
    .option("--max-order-size <amount>", "Max single-order size in asset units (Hyperliquid Grid, default: 10000)")
    .option("--max-orders-per-tick <n>", "Max orders per tick (Hyperliquid Grid, default: 20)")
    .option("--asset-indices <list>", "Comma-separated perp asset indices (Hyperliquid Grid, default: 0)")
    .action(async (templateKey: string, opts) => {
      const vault = opts.vault as Address;
      if (!isAddress(vault)) {
        console.error(chalk.red("Invalid vault address"));
        process.exit(1);
      }

      const { def, address: templateAddr } = resolveTemplate(templateKey);

      // --calldata-only: deploy the clone DETERMINISTICALLY (CREATE2 via
      // StrategyFactory) so its address is known up front, then emit BOTH the
      // clone+init tx and the propose tx as unsigned calldata. propose only
      // stores the strategy address (it doesn't call the clone), so the two txs
      // just need to land before the proposal executes. Template-agnostic — the
      // only per-template piece is initData. Closes the #387 keyless gap.
      if (isCalldataOnly()) {
        // Note: --name / --description are NOT required here — in calldata-only
        // mode the proposal metadata is supplied via --metadata-uri (the JSON it
        // points at carries the name/description). They have no effect on the
        // emitted calldata, so requiring them would just be a dead gate.
        // Note: no --performance-fee here. Under per-vault governance the agent
        // fee is vault-owner-set (setAgentFeeBps) and snapshotted from the
        // vault's agentFeeBps at propose time — propose() takes no fee argument.
        const missing: string[] = [];
        if (!opts.proposer) missing.push("--proposer <address> (the external signer)");
        if (!opts.metadataUri) missing.push("--metadata-uri <uri> (IPFS pin needs a signer)");
        if (!opts.duration) missing.push("--duration <d>");
        if (missing.length > 0) {
          console.error(chalk.red(`--calldata-only requires: ${missing.join(", ")}`));
          process.exit(1);
        }
        const proposer = opts.proposer as Address;
        if (!isAddress(proposer)) {
          console.error(chalk.red("Invalid --proposer address"));
          process.exit(1);
        }

        // Read-only preflight (no private key needed). Fail fast instead of
        // emitting calldata that will revert: cloneAndInitDeterministic + propose
        // both require the proposer to be a registered agent of the vault, and a
        // sub-balance vault reverts at execute. (The template-allowlist and
        // proposer == msg.sender checks can't be done off-chain — the emitted
        // note covers them: don't broadcast tx 2 unless tx 1 confirms.)
        try {
          const publicClient = getPublicClient();
          const [isRegistered, paused] = await Promise.all([
            publicClient.readContract({
              address: vault,
              abi: SYNDICATE_VAULT_ABI,
              functionName: "isAgent",
              args: [proposer],
            }) as Promise<boolean>,
            publicClient.readContract({
              address: vault,
              abi: SYNDICATE_VAULT_ABI,
              functionName: "paused",
            }) as Promise<boolean>,
          ]);
          if (!isRegistered) {
            console.error(
              chalk.red(
                `--proposer ${proposer} is not a registered agent on ${vault}; cloneAndInitDeterministic and propose will both revert.`,
              ),
            );
            console.error(chalk.yellow(`  Register first: sherwood fund approve --wallet ${proposer}`));
            process.exit(1);
          }
          if (paused) {
            console.error(chalk.red(`Vault ${vault} is paused — the proposal would revert.`));
            process.exit(1);
          }
          for (const check of computeProposalAssetChecks(templateKey, opts)) {
            if (check.amount === 0n) continue;
            const [balance, decimals] = await Promise.all([
              publicClient.readContract({
                address: check.asset,
                abi: erc20Abi,
                functionName: "balanceOf",
                args: [vault],
              }) as Promise<bigint>,
              publicClient.readContract({
                address: check.asset,
                abi: erc20Abi,
                functionName: "decimals",
              }) as Promise<number>,
            ]);
            if (balance < check.amount) {
              console.error(
                chalk.red(
                  `Vault ${vault} holds ${formatUnits(balance, decimals)} ${check.label} but the proposal needs ${formatUnits(check.amount, decimals)} ${check.label}.`,
                ),
              );
              console.error(chalk.yellow(`  Hint: --amount is in human units (e.g. "10" = 10 ${check.label}), not raw decimals.`));
              process.exit(1);
            }
          }
        } catch (err) {
          console.error(chalk.red(`Preflight read failed: ${err instanceof Error ? err.message : String(err)}`));
          process.exit(1);
        }

        // The venice builder defaults its agent to the signer's wallet; pin it to
        // the proposer so buildInitDataForTemplate never reaches for getAccount()
        // (there's no private key in calldata-only mode).
        if (!opts.agent) opts.agent = proposer;

        const { parseDuration, getGovernorOf } = await import("../lib/governor.js");
        const strategyDuration = parseDuration(opts.duration as string);
        const chainId = getChain().id as ChainId;

        try {
          // Per-vault governor: encodePropose targets the vault's own governor.
          const governor = await getGovernorOf(vault);
          const built = await buildInitDataForTemplate(templateKey, opts, vault);
          const deploy = encodeStrategyDeploy(
            {
              template: templateAddr,
              vault,
              proposer,
              initData: built.initData,
              salt: opts.salt as Hex | undefined,
            },
            chainId,
          );
          const { executeCalls, settleCalls } = buildCallsForTemplate(
            templateKey, deploy.clone, built.asset, built.assetAmount, built.extraApprovals,
          );
          const proposeAction = encodePropose(
            {
              vault,
              governor,
              strategy: deploy.clone,
              metadataURI: opts.metadataUri as string,
              strategyDuration,
              executeCalls,
              settlementCalls: settleCalls,
            },
            chainId,
          );
          emitCalldata({
            txs: [...deploy.txs, ...proposeAction.txs],
            preconditions: [...deploy.preconditions, ...proposeAction.preconditions],
            // Surface the deterministic clone + salt as structured fields (also
            // baked into tx 2's `strategy` arg + the execute/settle calls).
            clone: deploy.clone,
            salt: deploy.salt,
            description:
              `Keyless ${def.name} proposal on ${vault}: (1) deploy clone at ${deploy.clone} ` +
              `(deterministic CREATE2, salt ${deploy.salt}); (2) governor.propose referencing it.`,
            note:
              "Broadcast SEQUENTIALLY: send tx 1 (cloneAndInitDeterministic) and WAIT for it to confirm, then send tx 2 (propose). " +
              "If tx 1 reverts — proposer not a registered agent, proposer != tx-1 signer, template not allowlisted, or vault unregistered — do NOT send tx 2: it would create a proposal referencing an undeployed strategy. " +
              "Send tx 1 from the --proposer wallet (the factory requires proposer == msg.sender, and msg.sender must be a vault owner/agent). " +
              "The clone address is pinned by (factory, template, vault, salt) and is already baked into tx 2 + the execute/settle calls. " +
              "The factory binds the salt to the vault and only the vault's owner/agents may deploy there, so the predicted address can't be front-run.",
          } as Parameters<typeof emitCalldata>[0]);
        } catch (err) {
          console.error(chalk.red(err instanceof Error ? err.message : String(err)));
          process.exit(1);
        }
        return;
      }

      // 0. Preflight — bail out before burning gas on clone + init for a
      //    proposal the governor will refuse anyway. Two read-only checks,
      //    no gas cost.
      const account = getAccount();
      const preflightSpinner = ora("Preflight checks...").start();
      try {
        const publicClient = getPublicClient();

        // (a) Signer must be a registered active agent on this vault.
        const isRegistered = await publicClient.readContract({
          address: vault,
          abi: SYNDICATE_VAULT_ABI,
          functionName: "isAgent",
          args: [account.address],
        });
        if (!isRegistered) {
          preflightSpinner.fail("Preflight failed");
          console.error(
            chalk.red(
              `  Signer ${account.address} is not a registered agent on ${vault}.\n` +
                `  Register first via: sherwood fund approve --wallet ${account.address}`,
            ),
          );
          process.exit(1);
        }

        // (b) Vault must not be paused (deposits + governance ops would revert).
        const paused = await publicClient.readContract({
          address: vault,
          abi: SYNDICATE_VAULT_ABI,
          functionName: "paused",
        });
        if (paused) {
          preflightSpinner.fail("Preflight failed");
          console.error(chalk.red(`  Vault ${vault} is paused. Cannot propose.`));
          process.exit(1);
        }

        // (c) Vault must hold enough of the proposal's asset to fund execute.
        //     Catches the most common --amount footgun (passing raw decimal
        //     units, e.g. 10000000, when the CLI already applies parseUnits
        //     and the vault only holds 10 USDC). Without this guard the
        //     proposal still passes propose+vote but `_pullFromVault` reverts
        //     at execute with "ERC20: transfer amount exceeds balance".
        const checks = computeProposalAssetChecks(templateKey, opts);
        for (const check of checks) {
          if (check.amount === 0n) continue;
          const [balance, decimals] = await Promise.all([
            publicClient.readContract({
              address: check.asset,
              abi: erc20Abi,
              functionName: "balanceOf",
              args: [vault],
            }) as Promise<bigint>,
            publicClient.readContract({
              address: check.asset,
              abi: erc20Abi,
              functionName: "decimals",
            }) as Promise<number>,
          ]);
          if (balance < check.amount) {
            preflightSpinner.fail("Preflight failed");
            console.error(
              chalk.red(
                `  Vault ${vault} holds ${formatUnits(balance, decimals)} ${check.label} ` +
                  `but proposal requires ${formatUnits(check.amount, decimals)} ${check.label}.`,
              ),
            );
            console.error(
              chalk.yellow(
                `  Hint: --amount is in human units (e.g. \"10\" = 10 ${check.label}), not raw decimals.`,
              ),
            );
            process.exit(1);
          }
        }

        preflightSpinner.succeed("Preflight OK");
      } catch (err) {
        preflightSpinner.fail("Preflight failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // 1. Clone
      const cloneSpinner = ora(`Cloning ${def.name} template...`).start();
      let clone: Address;
      try {
        const result = await cloneTemplate(templateAddr);
        clone = result.clone;
        cloneSpinner.succeed(`Cloned: ${chalk.green(clone)}`);
        console.log(chalk.dim(`  Tx: ${getExplorerUrl(result.hash)}`));
      } catch (err) {
        cloneSpinner.fail("Clone failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // 2. Initialize
      const initSpinner = ora("Initializing strategy...").start();
      let asset: Address;
      let assetAmount: bigint;
      let extraApprovals: { token: Address; amount: bigint }[] | undefined;
      try {
        const built = await buildInitDataForTemplate(templateKey, opts, vault);
        asset = built.asset;
        assetAmount = built.assetAmount;
        extraApprovals = built.extraApprovals;

        // account hoisted above during preflight — reuse.
        const initHash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: clone,
          abi: BASE_STRATEGY_ABI,
          functionName: "initialize",
          args: [vault, account.address, built.initData],
        });

        const initReceipt = await waitForReceipt(initHash);
        if (initReceipt.status === "reverted") {
          throw new Error("Initialize transaction reverted on-chain");
        }
        initSpinner.succeed("Initialized");
      } catch (err) {
        initSpinner.fail("Initialize failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // 2b. HyperCore registration (grid + perp) — hard prerequisite before propose.
      // BaseStrategy.initialize() writes address(this) to slot 0 (_hcSelf). HC
      // reads slot 0 post-block via FirstStorageSlot variant. Without
      // registration the proposal will execute but the bridge can't credit
      // HC spot, burning a governance cycle on a doomed batch.
      if (templateKey === "hyperliquid-grid" || templateKey === "hyperliquid-perp") {
        const hcSpinner = ora("Registering clone with HyperCore...").start();
        try {
          const hcHash = await writeContractWithRetry({
            account,
            chain: getChain(),
            address: clone,
            abi: templateKey === "hyperliquid-grid" ? HYPERLIQUID_GRID_STRATEGY_ABI : HYPERLIQUID_PERP_STRATEGY_ABI,
            functionName: "finalizeForHyperCore",
            args: [0n, 1, 0n],  // token=USDC, variant=FirstStorageSlot(1), nonce unused
          });
          await waitForReceipt(hcHash);
          hcSpinner.succeed("HyperCore registration sent (active after next block)");
        } catch (err) {
          hcSpinner.fail("HyperCore registration failed — aborting proposal submission");
          console.error(chalk.red(formatContractError(err)));
          console.error(
            chalk.yellow(
              `Retry by calling finalizeForHyperCore(0, 1, 0) on ${clone}, then re-run propose.`,
            ),
          );
          process.exit(1);
        }
      }

      // 3. Build batch calls
      const { executeCalls, settleCalls } = buildCallsForTemplate(
        templateKey, clone, asset, assetAmount, extraApprovals,
      );

      console.log();
      console.log(chalk.bold(`Execute calls (${executeCalls.length}):`));
      console.log(formatBatch(executeCalls));
      console.log(chalk.bold(`Settle calls (${settleCalls.length}):`));
      console.log(formatBatch(settleCalls));

      // 4. Write files or submit proposal
      if (opts.writeCalls) {
        const dir = resolve(opts.writeCalls as string);
        mkdirSync(dir, { recursive: true });

        const execPath = resolve(dir, "execute.json");
        const settlePath = resolve(dir, "settle.json");
        writeFileSync(execPath, serializeCalls(executeCalls));
        writeFileSync(settlePath, serializeCalls(settleCalls));

        console.log();
        console.log(chalk.green(`Execute calls:  ${execPath}`));
        console.log(chalk.green(`Settle calls:   ${settlePath}`));
        console.log(chalk.green(`Clone address:  ${clone}`));
        console.log();
        console.log(chalk.dim("Submit with:"));
        console.log(chalk.dim(`  sherwood proposal create \\`));
        console.log(chalk.dim(`    --vault ${vault} \\`));
        console.log(chalk.dim(`    --name "..." --description "..." \\`));
        console.log(chalk.dim(`    --duration 7d \\`));
        console.log(chalk.dim(`    --execute-calls ${execPath} \\`));
        console.log(chalk.dim(`    --settle-calls ${settlePath}`));

        if (templateKey === "venice-inference") {
          console.log();
          console.log(chalk.yellow("Reminder: before settlement, agent must approve repayment:"));
          console.log(chalk.yellow(`  asset.approve(${clone}, <repaymentAmount>)`));
          console.log(chalk.yellow("  Agent can update repayment via strategy.updateParams(newRepayment, 0, 0)"));
        }

        console.log();
        return;
      }

      // Direct proposal submission requires metadata flags
      if (!opts.name || !opts.duration) {
        console.error(chalk.red("Missing --name or --duration. Use --write-calls to skip proposal submission."));
        process.exit(1);
      }

      // Lazy import governor to avoid pulling it in for --write-calls path
      const { propose } = await import("../lib/governor.js");
      const { pinJSON } = await import("../lib/ipfs.js");
      const { parseDuration } = await import("../lib/governor.js");

      const strategyDuration = parseDuration(opts.duration as string);
      // account already hoisted at preflight.

      const metaSpinner = ora("Pinning metadata to IPFS...").start();
      let metadataURI: string;
      try {
        const metadata = {
          name: opts.name,
          description: opts.description || "",
          proposer: account.address,
          vault,
          strategyClone: clone,
          template: def.key,
          strategyDuration: Number(strategyDuration),
          createdAt: new Date().toISOString(),
        };
        metadataURI = await pinJSON(metadata, opts.name as string);
        metaSpinner.succeed(`Metadata pinned: ${metadataURI}`);
      } catch (err) {
        metaSpinner.fail("IPFS pin failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      // M4: the agent inherits the vault's current agentFeeBps (no per-proposal
      // flag). Surface it so the agent can change it first if that's intended.
      try {
        const { getAgentFeeBps } = await import("../lib/vault.js");
        const feeBps = await getAgentFeeBps(vault);
        console.log(
          chalk.yellow(
            `  Agent fee for this proposal: ${Number(feeBps) / 100}% (the vault's current agentFeeBps). ` +
              "Run `sherwood fund set-agent-fee --bps <bps>` first to change it.",
          ),
        );
      } catch {
        /* non-fatal — the on-chain snapshot still applies at propose */
      }

      // Submit proposal with the freshly-cloned strategy address. Under the
      // strategy-on-proposal model the strategy is set immutably at propose
      // time — no separate bind step. If the strategy doesn't implement
      // `positionValue()` (legacy clones, perp-only) the vault's runtime
      // try/catch falls back to queue-only redemptions during execution.
      const proposeSpinner = ora("Submitting proposal...").start();
      try {
        const { hash, proposalId } = await propose(
          vault,
          clone,
          metadataURI,
          strategyDuration,
          executeCalls,
          settleCalls,
        );
        proposeSpinner.succeed(`Proposal #${proposalId} created — strategy=${clone} set on proposal`);
        console.log(`Proposal #${proposalId}`);
        console.log(chalk.dim(`  Tx: ${getExplorerUrl(hash)}`));
        console.log(chalk.dim(`  Clone: ${clone}`));
      } catch (err) {
        proposeSpinner.fail("Proposal failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }

      if (templateKey === "venice-inference") {
        console.log();
        console.log(chalk.yellow("Next steps:"));
        console.log(chalk.yellow("  1. After execution: sherwood venice provision"));
        console.log(chalk.yellow("  2. Use inference: sherwood venice infer --model <id> --prompt '...'"));
        console.log(chalk.yellow("  3. Before settlement: approve repayment (principal + profit):"));
        console.log(chalk.yellow(`     asset.approve(${clone}, <repaymentAmount>)`));
      }

      console.log();
    });

  // ── strategy status ──

  strategy
    .command("status")
    .description("Show portfolio strategy status — allocations, balances, drift, PnL (Portfolio only)")
    .argument("<clone>", "Strategy clone address")
    .option("--json", "Output machine-readable JSON for agent consumption")
    .action(async (cloneArg: string, opts) => {
      if (!isAddress(cloneArg)) {
        console.error(chalk.red("Invalid clone address"));
        process.exit(1);
      }
      const clone = cloneArg as Address;
      const publicClient = getPublicClient();
      const network = getNetwork();
      const jsonMode = !!opts.json;

      const spinner = jsonMode ? null : ora("Reading strategy state...").start();
      try {
        // Read strategy state + swap extra data (for fee tiers)
        const [stateRaw, vault, proposer, assetAddr, totalAmount, maxSlippage, allocations, swapExtraData] = await Promise.all([
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "state" }),
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "vault" }) as Promise<Address>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "proposer" }) as Promise<Address>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "asset" }) as Promise<Address>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "totalAmount" }) as Promise<bigint>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "maxSlippageBps" }) as Promise<bigint>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "getAllocations" }) as Promise<readonly { token: Address; targetWeightBps: bigint; tokenAmount: bigint; investedAmount: bigint }[]>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "getSwapExtraData" }) as Promise<readonly `0x${string}`[]>,
        ]);

        const stateNames = ["Pending", "Executed", "Settled"];
        const stateStr = stateNames[Number(stateRaw)] || `Unknown(${stateRaw})`;

        // Read asset decimals & symbol
        const [assetDecimals, assetSymbol] = await Promise.all([
          publicClient.readContract({ address: assetAddr, abi: erc20Abi, functionName: "decimals" }),
          publicClient.readContract({ address: assetAddr, abi: erc20Abi, functionName: "symbol" }),
        ]);

        // Read current token balances & symbols in parallel
        const tokenReads = allocations.map((a) => Promise.all([
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "balanceOf", args: [clone] }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "symbol" }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "decimals" }),
        ]));
        const tokenData = await Promise.all(tokenReads);

        // Read asset balance held by strategy
        const assetBalance = await publicClient.readContract({
          address: assetAddr,
          abi: erc20Abi,
          functionName: "balanceOf",
          args: [clone],
        });

        // Extract fee tiers from swapExtraData (abi.encode(uint24 fee))
        const feeTiers = swapExtraData.map((data) => {
          try {
            const [fee] = decodeAbiParameters([{ type: "uint24" }], data);
            return Number(fee) > 0 && Number(fee) <= 100000 ? Number(fee) : 3000;
          } catch {
            return 3000;
          }
        });

        // Fetch prices (graceful — continues without if unavailable)
        let prices: (import("../lib/price.js").TokenPrice | null)[] = [];
        let priceSource: "uniswap" | "synthra" | null = null;
        try {
          const { getTokenPricesInAsset } = await import("../lib/price.js");
          prices = await getTokenPricesInAsset({
            tokens: allocations.map((a, i) => ({
              token: a.token,
              tokenDecimals: tokenData[i][2] as number,
              feeTier: feeTiers[i],
            })),
            asset: assetAddr,
            assetDecimals,
          });
          priceSource = prices.find((p) => p !== null)?.source ?? null;
        } catch {
          // Prices unavailable — continue with balance-only
        }

        const pricesAvailable = prices.some((p) => p !== null);

        // Compute enriched allocation data
        const assetBalanceNum = Number(formatUnits(assetBalance as bigint, assetDecimals));
        let totalPortfolioValue = assetBalanceNum;
        let totalInvested = 0n;

        interface AllocStatus {
          token: Address;
          symbol: string;
          decimals: number;
          targetWeightBps: number;
          balance: string;
          balanceRaw: string;
          investedAmount: string;
          currentPrice: number | null;
          currentValue: number | null;
          actualWeightBps: number | null;
          driftBps: number | null;
          pnl: number | null;
          pnlPct: number | null;
        }

        const allocStatuses: AllocStatus[] = allocations.map((alloc, i) => {
          const [balance, symbol, decimals] = tokenData[i];
          const balanceNum = Number(formatUnits(balance as bigint, decimals));
          const investedNum = Number(formatUnits(alloc.investedAmount, assetDecimals));
          totalInvested += alloc.investedAmount;

          const price = prices[i];
          let currentValue: number | null = null;

          if (price) {
            currentValue = balanceNum * price.price;
            totalPortfolioValue += currentValue;
          }

          return {
            token: alloc.token,
            symbol: symbol as string,
            decimals: decimals as number,
            targetWeightBps: Number(alloc.targetWeightBps),
            balance: formatUnits(balance as bigint, decimals),
            balanceRaw: (balance as bigint).toString(),
            investedAmount: formatUnits(alloc.investedAmount, assetDecimals),
            currentPrice: price?.price ?? null,
            currentValue,
            actualWeightBps: null, // computed after totals
            driftBps: null,
            pnl: currentValue !== null ? currentValue - investedNum : null,
            pnlPct: currentValue !== null && investedNum > 0
              ? ((currentValue - investedNum) / investedNum) * 100
              : null,
          };
        });

        // Second pass: compute actual weights and drift (needs totalPortfolioValue)
        let maxDriftBps = 0;
        let maxDriftToken = "";
        if (pricesAvailable && totalPortfolioValue > 0) {
          for (const a of allocStatuses) {
            if (a.currentValue !== null) {
              a.actualWeightBps = Math.round((a.currentValue / totalPortfolioValue) * 10000);
              a.driftBps = a.actualWeightBps - a.targetWeightBps;
              if (Math.abs(a.driftBps) > Math.abs(maxDriftBps)) {
                maxDriftBps = a.driftBps;
                maxDriftToken = a.symbol;
              }
            }
          }
        }

        // Total PnL
        const totalInvestedNum = Number(formatUnits(totalInvested, assetDecimals));
        const totalPnl = pricesAvailable ? totalPortfolioValue - totalInvestedNum : null;
        const totalPnlPct = totalPnl !== null && totalInvestedNum > 0
          ? (totalPnl / totalInvestedNum) * 100
          : null;

        spinner?.succeed("Strategy state loaded");

        // ── JSON output ──
        if (jsonMode) {
          const result = {
            clone,
            vault,
            proposer,
            state: stateStr,
            network,
            asset: { address: assetAddr, symbol: assetSymbol as string, decimals: assetDecimals as number },
            totalDeployed: formatUnits(totalAmount, assetDecimals),
            maxSlippageBps: Number(maxSlippage),
            assetBalance: formatUnits(assetBalance as bigint, assetDecimals),
            pricesAvailable,
            priceSource,
            portfolio: {
              totalValue: pricesAvailable ? totalPortfolioValue : null,
              totalPnl,
              totalPnlPct,
            },
            allocations: allocStatuses,
            maxDriftBps: pricesAvailable ? maxDriftBps : null,
            maxDriftToken: pricesAvailable ? maxDriftToken : null,
            timestamp: new Date().toISOString(),
          };
          process.stdout.write(JSON.stringify(result) + "\n");
          return;
        }

        // ── Human-friendly output ──
        const stateColor = stateStr === "Executed" ? chalk.green : stateStr === "Settled" ? chalk.blue : chalk.yellow;

        console.log();
        console.log(chalk.bold("Portfolio Strategy Status"));
        console.log(chalk.dim("─".repeat(70)));
        console.log(`  Clone:       ${chalk.cyan(clone)}`);
        console.log(`  Vault:       ${chalk.cyan(vault)}`);
        console.log(`  Proposer:    ${chalk.cyan(proposer)}`);
        console.log(`  State:       ${stateColor(stateStr)}`);
        console.log(`  Asset:       ${assetSymbol} (${assetAddr})`);
        console.log(`  Deployed:    ${formatUnits(totalAmount, assetDecimals)} ${assetSymbol}`);
        console.log(`  Max Slip:    ${Number(maxSlippage) / 100}%`);
        console.log(`  Asset held:  ${formatUnits(assetBalance as bigint, assetDecimals)} ${assetSymbol}`);

        if (pricesAvailable) {
          const pvStr = totalPortfolioValue.toFixed(6);
          const pnlStr = totalPnl !== null ? (totalPnl >= 0 ? "+" : "") + totalPnl.toFixed(6) : "n/a";
          const pnlPctStr = totalPnlPct !== null ? (totalPnlPct >= 0 ? "+" : "") + totalPnlPct.toFixed(1) + "%" : "";
          const pnlColor = totalPnl !== null && totalPnl >= 0 ? chalk.green : chalk.red;
          console.log(`  Value:       ${pvStr} ${assetSymbol}  |  PnL: ${pnlColor(`${pnlStr} (${pnlPctStr})`)}`);
          console.log(`  Price src:   ${priceSource}`);
        }

        console.log();
        console.log(chalk.bold("Allocations"));
        console.log(chalk.dim("─".repeat(70)));

        if (pricesAvailable) {
          // Price-enriched table
          console.log(
            chalk.dim("  Token".padEnd(10)) +
            chalk.dim("Target".padEnd(9)) +
            chalk.dim("Actual".padEnd(9)) +
            chalk.dim("Drift".padEnd(8)) +
            chalk.dim("Value".padEnd(16)) +
            chalk.dim("PnL".padEnd(16)) +
            chalk.dim("Balance"),
          );

          for (const a of allocStatuses) {
            const targetStr = (a.targetWeightBps / 100).toFixed(1) + "%";
            const actualStr = a.actualWeightBps !== null ? (a.actualWeightBps / 100).toFixed(1) + "%" : "—";
            const driftStr = a.driftBps !== null
              ? (a.driftBps >= 0 ? "+" : "") + a.driftBps
              : "—";
            const driftColor = a.driftBps !== null
              ? (Math.abs(a.driftBps) > 500 ? chalk.red : Math.abs(a.driftBps) > 200 ? chalk.yellow : chalk.dim)
              : chalk.dim;
            const valueStr = a.currentValue !== null ? a.currentValue.toFixed(6) : "—";
            const pnlPctStr = a.pnlPct !== null ? (a.pnlPct >= 0 ? "+" : "") + a.pnlPct.toFixed(1) + "%" : "—";
            const pnlColor = a.pnlPct !== null ? (a.pnlPct >= 0 ? chalk.green : chalk.red) : chalk.dim;

            console.log(
              `  ${chalk.bold(a.symbol.padEnd(8))}` +
              `${targetStr.padEnd(9)}` +
              `${actualStr.padEnd(9)}` +
              `${driftColor(driftStr.toString().padEnd(8))}` +
              `${valueStr.padEnd(16)}` +
              `${pnlColor(pnlPctStr.padEnd(16))}` +
              `${a.balance}`,
            );
          }
        } else {
          // Balance-only table (no prices available)
          console.log(chalk.dim("  Prices unavailable — showing raw balances only."));
          console.log();
          console.log(
            chalk.dim("  Token".padEnd(12)) +
            chalk.dim("Weight".padEnd(10)) +
            chalk.dim("Invested".padEnd(18)) +
            chalk.dim("Balance".padEnd(18)) +
            chalk.dim("Address"),
          );

          for (const a of allocStatuses) {
            const weightPct = (a.targetWeightBps / 100).toFixed(1) + "%";
            console.log(
              `  ${chalk.bold(a.symbol.padEnd(10))}` +
              `${weightPct.padEnd(10)}` +
              `${a.investedAmount.padEnd(18)}` +
              `${a.balance.padEnd(18)}` +
              `${chalk.dim(a.token)}`,
            );
          }
        }

        console.log(chalk.dim("─".repeat(70)));
        console.log(`  Total invested: ${formatUnits(totalInvested, assetDecimals)} ${assetSymbol}`);

        if (pricesAvailable && maxDriftToken) {
          const absMaxDrift = Math.abs(maxDriftBps);
          const driftColor = absMaxDrift > 500 ? chalk.red : absMaxDrift > 200 ? chalk.yellow : chalk.green;
          console.log(`  Max drift:      ${driftColor(`${maxDriftBps > 0 ? "+" : ""}${maxDriftBps} bps`)} (${maxDriftToken})`);
        }

        if (stateStr === "Executed") {
          console.log();
          console.log(chalk.dim("Rebalance:  sherwood strategy rebalance " + clone));
          console.log(chalk.dim("With new weights: sherwood strategy rebalance " + clone + " --new-weights 2500,2500,2000,1500,1500"));
        }

        console.log();
      } catch (err) {
        spinner?.fail("Failed to read strategy state");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── strategy rebalance ──

  strategy
    .command("rebalance")
    .description("Rebalance a portfolio strategy — sell all positions, re-buy at target weights (Portfolio only)")
    // Note: this drives the strategy's simple `rebalance()` entrypoint (no price
    // reports). The oracle-delta path `rebalanceDelta(bytes[] priceReports)` is
    // not exposed as a command; in push-feed mode (Robinhood mainnet) it needs an
    // array of N EMPTY bytes entries (N = basket size) — build that calldata via
    // `portfolioBuilder.encodeRebalanceDeltaCalldata({ basketSize, pushMode: true })`.
    .argument("<clone>", "Strategy clone address")
    .option("--new-weights <list>", "Comma-separated new weights in bps (must sum to 10000). Updates weights before rebalancing.")
    .option("--max-slippage <bps>", "New max slippage bps (used with --new-weights)")
    .option("--dry-run", "Show what would happen without executing")
    .action(async (cloneArg: string, opts) => {
      if (!isAddress(cloneArg)) {
        console.error(chalk.red("Invalid clone address"));
        process.exit(1);
      }
      const clone = cloneArg as Address;
      const publicClient = getPublicClient();
      const account = getAccount();
      const chain = getChain();

      // 1. Verify strategy state
      const verifySpinner = ora("Verifying strategy state...").start();
      try {
        const [stateRaw, proposer, assetAddr, allocations] = await Promise.all([
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "state" }),
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "proposer" }) as Promise<Address>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "asset" }) as Promise<Address>,
          publicClient.readContract({ address: clone, abi: PORTFOLIO_STRATEGY_ABI, functionName: "getAllocations" }) as Promise<readonly { token: Address; targetWeightBps: bigint; tokenAmount: bigint; investedAmount: bigint }[]>,
        ]);

        // Check state
        if (Number(stateRaw) !== 1) { // State.Executed = 1
          const stateNames = ["Pending", "Executed", "Settled"];
          verifySpinner.fail(`Strategy is ${stateNames[Number(stateRaw)] || "Unknown"}, must be Executed to rebalance`);
          process.exit(1);
        }

        // Check proposer
        if (proposer.toLowerCase() !== account.address.toLowerCase()) {
          verifySpinner.fail(`Only the proposer (${proposer}) can rebalance. Your wallet: ${account.address}`);
          process.exit(1);
        }

        const [assetDecimals, assetSymbol] = await Promise.all([
          publicClient.readContract({ address: assetAddr, abi: erc20Abi, functionName: "decimals" }),
          publicClient.readContract({ address: assetAddr, abi: erc20Abi, functionName: "symbol" }),
        ]);

        // Read current balances
        const tokenReads = allocations.map((a) => Promise.all([
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "balanceOf", args: [clone] }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "symbol" }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "decimals" }),
        ]));
        const tokenData = await Promise.all(tokenReads);

        verifySpinner.succeed("Strategy is Executed — ready to rebalance");

        // Show current allocations
        console.log();
        console.log(chalk.bold("Current Allocations"));
        console.log(chalk.dim("─".repeat(50)));
        for (let i = 0; i < allocations.length; i++) {
          const alloc = allocations[i];
          const [balance, symbol, decimals] = tokenData[i];
          const weightPct = (Number(alloc.targetWeightBps) / 100).toFixed(1) + "%";
          console.log(
            `  ${chalk.bold((symbol as string).padEnd(8))} ` +
            `${weightPct.padEnd(8)} ` +
            `bal: ${formatUnits(balance as bigint, decimals)}`,
          );
        }

        // Parse new weights if provided
        let newWeightsBps: number[] | undefined;
        if (opts.newWeights) {
          newWeightsBps = (opts.newWeights as string).split(",").map((w) => Number(w.trim()));
          if (newWeightsBps.length !== allocations.length) {
            console.error(chalk.red(`--new-weights must have ${allocations.length} values (one per token)`));
            process.exit(1);
          }
          const weightSum = newWeightsBps.reduce((a, b) => a + b, 0);
          if (weightSum !== 10000) {
            console.error(chalk.red(`Weights must sum to 10000, got ${weightSum}`));
            process.exit(1);
          }

          console.log();
          console.log(chalk.bold("New Target Weights"));
          console.log(chalk.dim("─".repeat(50)));
          for (let i = 0; i < allocations.length; i++) {
            const [, symbol] = tokenData[i];
            const oldPct = (Number(allocations[i].targetWeightBps) / 100).toFixed(1);
            const newPct = (newWeightsBps[i] / 100).toFixed(1);
            const arrow = oldPct !== newPct ? chalk.yellow("→") : chalk.dim("→");
            console.log(`  ${chalk.bold((symbol as string).padEnd(8))} ${oldPct}% ${arrow} ${newPct}%`);
          }
        }

        if (opts.dryRun) {
          console.log();
          console.log(chalk.yellow("Dry run — no transactions sent."));
          console.log(chalk.dim("The rebalance would:"));
          console.log(chalk.dim("  1. Sell all current token positions back to " + assetSymbol));
          console.log(chalk.dim("  2. Re-buy at " + (newWeightsBps ? "new" : "current") + " target weights"));
          console.log();
          return;
        }

        // 2. Update weights first (if specified)
        if (newWeightsBps) {
          const maxSlip = Number((opts.maxSlippage as string) || "0");
          const swapData = await publicClient.readContract({
            address: clone,
            abi: PORTFOLIO_STRATEGY_ABI,
            functionName: "getSwapExtraData",
          }) as Hex[];

          const updateSpinner = ora("Updating target weights...").start();
          try {
            const innerData = encodeAbiParameters(
              [{ type: "uint256[]" }, { type: "uint256" }, { type: "bytes[]" }],
              [newWeightsBps.map((w: number) => BigInt(w)), BigInt(maxSlip), swapData],
            );

            const updateHash = await writeContractWithRetry({
              account,
              chain,
              address: clone,
              abi: PORTFOLIO_STRATEGY_ABI,
              functionName: "updateParams",
              args: [innerData],
            });

            const receipt = await waitForReceipt(updateHash);
            if (receipt.status === "reverted") throw new Error("updateParams reverted");
            updateSpinner.succeed("Target weights updated");
            console.log(chalk.dim(`  Tx: ${getExplorerUrl(updateHash)}`));
          } catch (err) {
            updateSpinner.fail("Failed to update weights");
            console.error(chalk.red(formatContractError(err)));
            process.exit(1);
          }
        }

        // 3. Execute rebalance
        const rebalanceSpinner = ora("Rebalancing portfolio (sell all → re-buy at targets)...").start();
        try {
          const rebalanceHash = await writeContractWithRetry({
            account,
            chain,
            address: clone,
            abi: PORTFOLIO_STRATEGY_ABI,
            functionName: "rebalance",
          });

          const receipt = await waitForReceipt(rebalanceHash);
          if (receipt.status === "reverted") throw new Error("rebalance() reverted on-chain");
          rebalanceSpinner.succeed("Portfolio rebalanced");
          console.log(chalk.dim(`  Tx: ${getExplorerUrl(rebalanceHash)}`));
        } catch (err) {
          rebalanceSpinner.fail("Rebalance failed");
          console.error(chalk.red(formatContractError(err)));
          process.exit(1);
        }

        // 4. Show updated balances
        const postSpinner = ora("Reading updated allocations...").start();
        const postAllocations = await publicClient.readContract({
          address: clone,
          abi: PORTFOLIO_STRATEGY_ABI,
          functionName: "getAllocations",
        }) as readonly { token: Address; targetWeightBps: bigint; tokenAmount: bigint; investedAmount: bigint }[];

        const postReads = postAllocations.map((a) => Promise.all([
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "balanceOf", args: [clone] }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "symbol" }),
          publicClient.readContract({ address: a.token, abi: erc20Abi, functionName: "decimals" }),
        ]));
        const postData = await Promise.all(postReads);
        postSpinner.succeed("Updated allocations");

        console.log();
        console.log(chalk.bold("Post-Rebalance Allocations"));
        console.log(chalk.dim("─".repeat(50)));
        for (let i = 0; i < postAllocations.length; i++) {
          const alloc = postAllocations[i];
          const [balance, symbol, decimals] = postData[i];
          const weightPct = (Number(alloc.targetWeightBps) / 100).toFixed(1) + "%";
          const investedStr = formatUnits(alloc.investedAmount, assetDecimals);
          console.log(
            `  ${chalk.bold((symbol as string).padEnd(8))} ` +
            `${weightPct.padEnd(8)} ` +
            `invested: ${investedStr.padEnd(14)} ` +
            `bal: ${formatUnits(balance as bigint, decimals)}`,
          );
        }

        console.log();
        console.log(chalk.green("✓ Rebalance complete"));
        console.log();
      } catch (err: any) {
        if (err.code === "ERR_MODULE_NOT_FOUND" || err instanceof TypeError) throw err;
        verifySpinner.fail("Pre-flight check failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });
}
